import Season, { Exam, ExamQuestion, Question } from "../../../domain/Season";
import { useReducer } from "react";
import { UUID } from "../../../domain/UUID";
import ExamLevel from "../../../domain/ExamLevel";
import workingSeasonReducer from "./workingSeasonReducer";
import { PutSeasonQuestionsRequest } from "../../../gateway/exams";

type QuestionField = 'name' | 'number' | 'maximumPoints';

/**
 * Natural sort for question numbers with special rule:
 * Questions with alphabetic prefix (e.g., "A1", "B2") come first
 * Questions without prefix (e.g., "1", "2") come last
 * Within each group, sort by prefix then by number
 */
function naturalSort(a: string, b: string): number {
  const parseQuestionNumber = (str: string): { prefix: string, number: number } => {
    const match = str.match(/^([A-Za-z]*)(\d+)$/);
    if (match) {
      return {
        prefix: match[1],
        number: parseInt(match[2])
      };
    }
    // Fallback for unexpected formats
    return { prefix: str, number: 0 };
  };

  const aParts = parseQuestionNumber(a);
  const bParts = parseQuestionNumber(b);

  // Questions with prefix come before questions without prefix
  const aHasPrefix = aParts.prefix.length > 0;
  const bHasPrefix = bParts.prefix.length > 0;

  if (aHasPrefix && !bHasPrefix) {
    return -1;
  }
  if (!aHasPrefix && bHasPrefix) {
    return 1;
  }

  // Both have prefix or both don't have prefix
  // First compare prefixes (alphabetically)
  const prefixCompare = aParts.prefix.localeCompare(bParts.prefix);
  if (prefixCompare !== 0) {
    return prefixCompare;
  }

  // Prefixes are equal, compare numeric parts
  return aParts.number - bParts.number;
}

export interface WorkingSeason {
  readonly seasonView: Season,
  readonly reset: (season?: Season) => void
  readonly toPutSeasonQuestionsRequest: () => PutSeasonQuestionsRequest,
  readonly onAddQuestion: (question: Question) => void,
  readonly onDeleteQuestion: (questionId: UUID<Question>) => void,
  readonly onChangeQuestion: (questionId: UUID<Question>, field: QuestionField, value: string | number) => void,
  readonly onAssignQuestion: (questionId: UUID<Question>, examId: UUID<Exam>) => void,
  readonly onUnassignQuestion: (questionId: UUID<Question>, examId: UUID<Exam>) => void,
  readonly onChangeAssignedMarks: (questionId: UUID<Question>, examId: UUID<Exam>, marks: number) => void
}

export interface ExamAssignment {
  readonly examId: UUID<Exam>,
  readonly questionId: UUID<Question>,
  readonly maximumMarks: number
}

export interface WorkingSeasonState {
  readonly initialSeason: Season,
  readonly questions: Map<UUID<Question>, Question>,
  readonly examAssignments: ExamAssignment[]
}

function examAssignments(season: Season): ExamAssignment[] {
  const assignments: ExamAssignment[] = [];

  for (let level in season.exams) {
    const exam = season.exams[level as ExamLevel];

    for (let question of exam.questions) {
      assignments.push({ examId: exam.examId, questionId: question.id, maximumMarks: question.maximumMarks });
    }
  }

  return assignments;
}

export function workingSeasonState(season: Season): WorkingSeasonState {
  const questions: Map<UUID<Question>, Question> = new Map<UUID<Question>, Question>();

  for (let question of season.questions) {
    questions.set(question.id, { ...question });
  }

  return { initialSeason: season, questions, examAssignments: examAssignments(season) }
}

function asSeason(workingSeason: WorkingSeasonState): Season {
  // @ts-ignore
  const exams: { [K in ExamLevel]: Exam } = {};

  for (let level in workingSeason.initialSeason.exams) {
    const initialExam = workingSeason.initialSeason.exams[level as ExamLevel];

    const examQuestions: ExamQuestion[] = workingSeason.examAssignments
      .filter(ea => ea.examId === initialExam.examId)
      .map((ea): [ExamAssignment, Question | undefined] => [ea, workingSeason.questions.get(ea.questionId)])
      .filter(([ea, q]) => q !== undefined)
      .map(([ea, q]): [ExamAssignment, Question] => {
        // @ts-ignore
        return [ea, q];
      })
      .map(([ea, q]) => {
        return {
          id: q.id,
          name: q.name,
          number: q.number,
          maximumPoints: q.maximumPoints,
          maximumMarks: ea.maximumMarks
        }
      });

    exams[level as ExamLevel] = {
      ...initialExam,
      questions: examQuestions
    }
  }

  const sortedQuestions: Question[] = [];
  workingSeason.questions.forEach(v => sortedQuestions.push(v));
  sortedQuestions.sort((a, b) => naturalSort(a.number, b.number));

  return {
    ...workingSeason.initialSeason,
    questions: sortedQuestions,
    exams
  };
}

export default function useWorkingSeason(initialSeason: Season): WorkingSeason {
  const [state, dispatch] = useReducer(workingSeasonReducer, initialSeason, workingSeasonState);

  let seasonView = asSeason(state);

  return {
    seasonView,
    reset: (season) => dispatch({ type: "RESET", value: season }),
    toPutSeasonQuestionsRequest: () => ({ questions: seasonView.questions, examAssignments: state.examAssignments }),
    onAddQuestion: question => dispatch({ type: "ADD_QUESTION", value: question }),
    onDeleteQuestion: questionId => dispatch({ type: "DELETE_QUESTION", value: questionId }),
    onChangeQuestion: (questionId, field, value) => {
      dispatch({ type: "CHANGE_QUESTION", value: { questionId, field, value } })
    },
    onAssignQuestion: (questionId, examId) => dispatch({ type: "ASSIGN_QUESTION", value: { questionId, examId } }),
    onUnassignQuestion: (questionId, examId) => dispatch({ type: "UNASSIGN_QUESTION", value: { questionId, examId } }),
    onChangeAssignedMarks: (questionId, examId, marks) => dispatch({
      type: "CHANGE_ASSIGNED_MARKS",
      value: { questionId, examId, marks }
    })
  };
}
